'use client'

import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	useCallback,
	useMemo,
	ReactNode,
} from 'react'
import { useSession } from 'next-auth/react'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { StorageKeys } from '@/lib/constants/storagekeys'
import {
	getCompanyGoals,
	putCompanyGoals,
} from '@/app/service/companyService'
import {
	CompanyGoal,
	CompanyGoalsResponse,
} from '@/app/service/models/companyDetails'
import {
	ExpApiError,
	extractApiErrorDetails,
} from '@/app/lib/errors/apiError'
import { ExpApiErrorObject } from '@/app/lib/errors/models/errorTypes'
import { useCompanyContext } from '@/lib/context/companyContext'
import { PERMISSIONS } from '@/lib/constants/permissions'

interface GoalContextType {
	goals: CompanyGoal[]
	isGoalsLoading: boolean
	goalsError: Error | ExpApiErrorObject | ExpApiError | undefined
	refreshGoals: () => Promise<void>
	updateGoal: (goal: CompanyGoal, companyId: string | null) => Promise<boolean>
}

const GoalContext = createContext<GoalContextType | undefined>(undefined)

export const GoalProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const { data: session } = useSession()
	const { selectedCompanyId, permissions } = useCompanyContext()
	const { tempBoss2102EnableMultiCompanyPage } = useFlags()

	const [goals, setGoals] = useState<CompanyGoal[]>([])
	const [isGoalsLoading, setIsGoalsLoading] = useState(false)
	const [goalsError, setGoalsError] = useState<
		Error | ExpApiErrorObject | ExpApiError | undefined
	>()

	const refreshGoals = useCallback(async () => {
		const localStorageCompanyId =
			localStorage.getItem(StorageKeys.COOKIE_COMPANY_ID) ?? ''

		const targetCompanyId = tempBoss2102EnableMultiCompanyPage
			? selectedCompanyId
			: localStorageCompanyId || selectedCompanyId // Fallback to state if storage emptys

		if (!targetCompanyId || !session?.accessToken) {
			console.warn('GoalProvider - No companyId or access token available')
			return
		}
		setIsGoalsLoading(true)
		setGoalsError(undefined)
		try {
			const response = await getCompanyGoals(
				targetCompanyId,
				session.accessToken
			)

			if (!response) {
				// getCompanyGoals swallows errors and returns undefined on failure
				setGoalsError(new Error('No response from goals API'))
				return
			}

			const goalsData = response as CompanyGoalsResponse
			// Handle both possible response formats: goals or companyGoals
			setGoals(goalsData.goals || goalsData.companyGoals || [])
		} catch (err) {
			const errorDetails = extractApiErrorDetails(
				err,
				'Error fetching company goals in GoalProvider'
			)
			setGoalsError(errorDetails)
			console.error('GoalProvider - Error fetching goals:', errorDetails)
		} finally {
			setIsGoalsLoading(false)
		}
	}, [
		selectedCompanyId,
		session?.accessToken,
		tempBoss2102EnableMultiCompanyPage,
	])

	const updateGoal = useCallback(
		async (
			goal: CompanyGoal,
			targetCompanyId: string | null
		): Promise<boolean> => {
			if (!targetCompanyId || !session?.accessToken) {
				console.warn('GoalProvider - No companyId or access token available')
				return false
			}

			try {
				setGoalsError(undefined)
				await putCompanyGoals(targetCompanyId, goal, session.accessToken)

				// Update local state
				setGoals(prevGoals => {
					const updatedGoals = prevGoals.filter(g => g.yearNo !== goal.yearNo)
					return [...updatedGoals, goal].sort((a, b) => b.yearNo - a.yearNo)
				})

				return true
			} catch (err) {
				const errorMessage = extractApiErrorDetails(
					err,
					'Failed to update company goal'
				)
				console.error('GoalProvider - Error updating goal:', errorMessage)
				setGoalsError(errorMessage)
				return false
			}
		},
		[session?.accessToken]
	)

	// Fetch goals when permissions are (re-)loaded — only if user has goals permission.
	// IMPORTANT: selectedCompanyId is intentionally NOT in the deps array.
	// When a user switches companies, selectedCompanyId changes first while
	// permissions still reflects the OLD company. Depending on selectedCompanyId
	// would fire the effect with new-company + old-permissions, causing
	// unauthorized API calls. Instead, we trigger on `permissions` which updates
	// AFTER UserContext refreshes for the new company.
	const canViewGoals = permissions?.includes(PERMISSIONS.OT_GOALS_READ) ?? false

	useEffect(() => {
		if (!session?.accessToken || !selectedCompanyId || !canViewGoals) {
			return () => {}
		}

		let isCancelled = false

		const runRefresh = async () => {
			if (isCancelled) {
				return
			}
			await refreshGoals()
		}

		runRefresh()

		return () => {
			isCancelled = true
		}
		// Note: refreshGoals and selectedCompanyId are intentionally excluded.
		// refreshGoals' own deps (session?.accessToken, selectedCompanyId,
		// tempBoss2102EnableMultiCompanyPage) overlap with these deps.
		// selectedCompanyId is excluded to avoid the stale-permissions race
		// condition described above — permissions change is the trigger.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [session?.accessToken, tempBoss2102EnableMultiCompanyPage, canViewGoals])

	const contextValue = useMemo(
		() => ({
			goals,
			isGoalsLoading,
			goalsError,
			refreshGoals,
			updateGoal,
		}),
		[goals, isGoalsLoading, goalsError, refreshGoals, updateGoal]
	)

	return (
		<GoalContext.Provider value={contextValue}>
			{children}
		</GoalContext.Provider>
	)
}

const defaultGoalContext: GoalContextType = {
	goals: [],
	isGoalsLoading: false,
	goalsError: undefined,
	refreshGoals: async () => {},
	updateGoal: async () => false,
}

export const useGoalContext = (): GoalContextType => {
	const context = useContext(GoalContext)
	if (context === undefined) {
		console.warn(
			'useGoalContext called outside GoalProvider — returning safe defaults'
		)
		return defaultGoalContext
	}
	return context
}
